import React, { useEffect, useMemo, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Icons as GridIcons,
  Column,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { Customer } from "../../../../../../../models/customer";
import { Project } from "../../../../../../../models/project";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import { ProjectEmptySection } from "./components/EmptySection";
import ProjectManageModal from "./components/ManageModal";
import { useActionProject } from "./hooks/useAction";
import { BadgeActive, BadgeInactive, ChangeAllStatusButton, FiEyeButton } from "./styles";

interface ProjectsPageComponentProps {
  customer?: Customer;
}

const ProjectsPage = ({ customer }: ProjectsPageComponentProps) => {
  const { t: translate } = useTranslation();

  const {
    variables,
    handleChangeProject,
    setProject,
    createProject,
    updateProject,
    setHasErrors,
    setCurrentPage,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSort,
  } = useActionProject({ customer });

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => {
    if (status === Status.ACTIVE) {
      return <BadgeActive>{status}</BadgeActive>;
    }

    return <BadgeInactive>{status}</BadgeInactive>;
  };

  const editProject = (index: number) => {
    if (variables.hasErrors !== undefined) setHasErrors(undefined);
    let projectList: Project[] = [];

    try {
      projectList = JSON.parse(JSON.stringify(variables.projectList));
    } catch {
      projectList = [...variables.projectList];
    }
    const find = projectList.find((branch) => Number(branch.id) === Number(index));

    if (find) {
      setProject(find);
      setIsOnViewMode(false);
      setModalOpened(true);
    }
  };

  const viewProject = (index: number) => (
    <Tooltip title="View" placement="top">
      <div>
        <FiEyeButton
          size={18}
          onClick={() => {
            if (variables.hasErrors !== undefined) setHasErrors(undefined);
            const find = variables.projectList.find(
              (project) => Number(project.id) === Number(index)
            );

            if (find) {
              setProject(find);
              setIsOnViewMode(true);
              setModalOpened(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const changeProjectStatus = (index: number) => {
    const find = variables.projectList.find((project) => Number(project.id) === Number(index));

    if (find) {
      updateProject({
        projectToUpdate: {
          ...find,
          status: find.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows?.length > 0) {
      return (
        <ChangeAllStatusButton
          tooltip={translate("changeStatus")}
          data-cy="inactiveStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={17} className="mr-2" />
          {translate("changeStatus")}
        </ChangeAllStatusButton>
      );
    }

    return null;
  };

  const handleSaveProjectSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, variables.projectList);

    setRows([...rowsSelected]);
  };

  const formatDate = (value: string) => value && moment(value).format("DD/MM/YYYY");
  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "customerName",
      title: translate("customerName"),
      formatText: () => customer?.fullName,
    },
    {
      name: "startDate",
      title: translate("startDate"),
      formatText: formatDate,
      hideSearch: true,
    },
    {
      name: "endDate",
      title: translate("endDate"),
      formatText: formatDate,
      hideSearch: true,
    },

    {
      name: "status",
      title: translate("status"),
      formatComponent: (value) => badgeStatus(value),
      hideSearch: true,
    },
  ];

  const isThereData = useMemo(
    () => !!variables?.projectList?.length,
    [variables?.projectList?.length]
  );

  const generalActions: GeneralActions = {
    orderBy: {
      left: isThereData ? ["handleSearch", "handleFilterBy"] : [],
      right: isThereData
        ? ["handleActive", variables?.projectList?.length ? "handleNewRecord" : ""]
        : [],
    },
    handleNewRecord: {
      text: translate("newProject"),
      action: () => {
        if (variables.hasErrors !== undefined) setHasErrors(undefined);
        setIsOnViewMode(false);
        setProject(undefined);
        setModalOpened(true);
      },
      tooltip: translate("newProject"),
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("beCode"),
          column: "beCode",
        },
        {
          name: translate("name"),
          column: "name",
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    handleActive: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (variables.hasErrors !== undefined && !variables.hasErrors) {
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [variables.hasErrors]);

  const renderContent = () => {
    return (
      <ShipeeziNewGrid
        data={variables.projectList}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isLoadingProjects || variables.isCreating || variables.isUpdating}
        emptyData={() => (
          <ProjectEmptySection
            buttonLabel={translate("newProject")}
            descriptionScreen={`${translate(
              "youWillBeAbleToSeeAllTheInformationAboutThe"
            )} ${translate("projects").toLowerCase()} ${translate("whenYouAddThem")}`}
            onAdd={() => setModalOpened(true)}
          />
        )}
        pageSize={variables.pageSize}
        pageSizeList={[15, 40, 60, 100]}
        currentPage={variables.page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveProjectSelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit: (projectId) => editProject(Number(projectId)),
          handleChange: (projectId) => changeProjectStatus(Number(projectId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewProject }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
    );
  };

  return (
    <>
      {renderContent()}
      {modalOpened && (
        <ProjectManageModal
          handleChangeProject={handleChangeProject}
          setProject={setProject}
          projectToUpdate={variables.project}
          isOpened={modalOpened}
          isLoading={variables.isUpdating || variables.isCreating}
          isView={isOnViewMode}
          onCloseModal={() => setModalOpened(false)}
          onSubmit={(project: Project) => {
            if (project.id) {
              updateProject(
                {},
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
            } else
              createProject(
                {},
                {
                  onSuccess: () => {
                    setModalOpened(false);
                  },
                }
              );
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          closeModal={() => setOpenConfirmation(false)}
          rows={rows}
          isLoading={variables.isUpdating}
          hasErrors={variables.hasErrors}
          update={(rowId, status) => {
            const projectToUpdateStatus = variables.projectList.find(
              (project) => project.id === rowId
            );

            if (rowId) {
              updateProject({ projectToUpdate: { ...projectToUpdateStatus, status } as Project });
              setClearSelection(true);
            }
          }}
        />
      )}
    </>
  );
};

export default ProjectsPage;
